/**
 * Google Places autocomplete session tokens.
 *
 * Without a token, every keystroke's autocomplete request is billed separately.
 * With one, Google groups all the requests for a single address entry plus the
 * closing Place Details call into one session and charges nothing for the
 * autocomplete calls — only the details call is billed. That makes the details
 * lookup cheaper overall than the per-request billing it replaces.
 *
 * The rules that matter:
 *  - one fresh token per address entry,
 *  - the same token on every autocomplete call and on the details call,
 *  - discard it once details has been fetched. Reusing a token past that point
 *    silently drops the session back to per-request billing.
 */

/** Random token; falls back to Math.random where crypto.randomUUID is absent. */
const newToken = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

/**
 * Tracks one token per key, so a form with several address fields keeps their
 * sessions separate. `store` is a plain object owned by the caller (typically a
 * ref), which keeps this module free of shared mutable state.
 */
export const getSessionToken = (store, key) => {
    if (!store[key]) {
        store[key] = newToken();
    }
    return store[key];
};

/** Ends the session for `key`; the next getSessionToken call starts a new one. */
export const clearSessionToken = (store, key) => {
    delete store[key];
};
